// Keeps the app's old name on purpose — renaming the key would re-run the tour
// for everyone who has already sat through it. See localCache.ts.
const DISMISSED_KEY = 'cashflow-calendar:tutorial-dismissed:v1'

export function hasTutorialBeenDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return true
  }
}

export function dismissTutorial(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // localStorage unavailable — nothing to persist, tutorial will just re-show next time
  }
}

import { SidebarTab } from '../types'

export type TutorialView = 'overview' | 'year' | 'month' | 'sandbox'

export interface TutorialStep {
  /** matches a data-tour="..." attribute in the DOM */
  target: string
  title: string
  body: string
  /** switch to this view when the step is entered, if it isn't already active */
  view?: TutorialView
  /** switch the sidebar to this tab — its panel only exists in the DOM while selected */
  tab?: SidebarTab
  /**
   * True when the target lives in the sidebar. On a phone the sidebar is a
   * drawer, so it has to be opened before the step has anything to point at.
   */
  sidebar?: boolean
}

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    target: 'starting-balance',
    sidebar: true,
    title: 'Accounts and starting balance',
    body: "Pick an account here, or add another — a joint account, savings — each with its own plan. Enter each account's current balance and the date it's accurate as of: everything is projected forward from that point. Edit account updates it any time.",
  },
  {
    target: 'sidebar-tabs',
    sidebar: true,
    title: 'Your plan, in tabs',
    body: 'Everything that feeds the forecast lives here — income, outgoings, budgets, one-offs, adjustments and targets. Pick a tab to work on one list at a time.',
  },
  {
    target: 'income',
    sidebar: true,
    title: 'Income',
    tab: 'income',
    body: 'Add recurring income, like your salary. Set how often it lands — monthly, weekly, every four weeks, or yearly — and the date or day it arrives on.',
  },
  {
    target: 'outgoings',
    sidebar: true,
    title: 'Fixed outgoings',
    tab: 'expenses',
    body: "Bills and subscriptions that recur on a schedule: rent, phone, Netflix. If one is a loan or other debt, flag it to add an interest rate and unlock the payoff calculator.",
  },
  {
    target: 'budgets',
    sidebar: true,
    title: 'Budgeted spend',
    tab: 'budgets',
    body: "Variable costs you don't have an exact bill for — groceries, fuel, going out. Give it a forecast amount, and record what each one really cost under \"Budget vs actual\" to track the running variance.",
  },
  {
    target: 'oneoffs',
    sidebar: true,
    title: 'One-offs',
    tab: 'oneoffs',
    body: "A single income or expense on a specific date that won't repeat — a tax refund, a car repair, a holiday.",
  },
  {
    target: 'adjustments',
    sidebar: true,
    title: 'Balance adjustments',
    tab: 'adjustments',
    body: "When real life deviates from the plan, record a dated correction with the reason why. It acts as a new starting balance from that date, and the strict toggle here flips between the as-budgeted plan and what actually happened.",
  },
  {
    target: 'targets',
    sidebar: true,
    title: 'Targets',
    tab: 'targets',
    body: "The RAG colours mark danger; targets mark a level worth reaching. Set a balance to hit, optionally how long it must be held, and optionally an amount to raise the bar by each time you sustain it.",
  },
  {
    target: 'calendar',
    title: 'Day-by-day view',
    view: 'month',
    body: "Each day is colour-coded by how healthy its closing balance is. Hover a day to see a quick preview, or click it (tap, on mobile) to open the full breakdown of that day's transactions.",
  },
  {
    target: 'view-switcher',
    title: 'Overview, year, month, sandbox',
    body: "The overview is a dashboard — where each account stands today, the lowest point ahead, and what's coming up. Then a full year, a single exploded month, and the sandbox. Choose All accounts to see every one of them added together.",
  },
  {
    target: 'sandbox-panel',
    title: 'Sandbox',
    view: 'sandbox',
    body: "A scratch copy of your plan, starting from today's actual balance — so changes only affect the plan from today forward, never your history. Toggle things off, tweak amounts, add hypothetical one-offs, and compare the result against your real plan on the chart. Nothing here touches your real data until you copy a change back yourself.",
  },
]
